import { getReputationTier } from '../../utils/reputationTiers';
import React, { useState, useEffect, useRef } from 'react';
import { Topic, Comment, User, BasemapId, TopicVisibility, UserProfile, ProfileTab, DraftItem } from '../../types';
import { ChatIcon, ShareIcon, EditIcon, DeleteIcon, MessagePlusIcon, ThumbtackIcon, LockIcon } from '../common/Icons';
import LinkifiedText from '../common/LinkifiedText';
import { usersApi, notificationsApi, adminApi, topicsApi } from '../../services/api';
import { savesApi, SaveList, Save } from '../../services/saves-api';
import { useNotifications } from '../Notifications/NotificationProvider';
import AccessRightsModal from '../ProfilePage/AccessRightsModal';

const normalizeVisibility = (visibility?: TopicVisibility) => visibility ?? 'PUBLIC';
const getTopicVisibility = (topic: { visibility?: TopicVisibility }) => normalizeVisibility(topic.visibility);
const getCommentVisibility = (comment: { topicVisibility?: TopicVisibility; topic?: { visibility?: TopicVisibility } }) =>
    normalizeVisibility(comment.topicVisibility ?? comment.topic?.visibility);
const isPublicTopic = (topic: { visibility?: TopicVisibility }) => getTopicVisibility(topic) === 'PUBLIC';
const isPublicComment = (comment: { topicVisibility?: TopicVisibility; topic?: { visibility?: TopicVisibility } }) =>
    getCommentVisibility(comment) === 'PUBLIC';

const ProfileContentPanel: React.FC<{
  currentUser?: User | null;
  profileUserId?: string | null;
  onMessageUser?: (userId: string) => void;
  onOpenMessages?: () => void;
  initialTab?: ProfileTab;
  topics?: Topic[];
  onTopicSelect?: (topic: Topic, focusCommentId?: string) => void;
  onCommentSelect?: (comment: Comment | null) => void;
  onCommentSelectById?: (commentId: string) => void;
  onCloseProfile?: () => void;
  userMenuEntry?: 'profile-panel' | 'profile-menu' | 'profile-external' | 'profile-from-messages';
  onSetProfileUserId?: (userId: string | null) => void;
  onSetUserMenuEntry?: (entry: 'profile-panel' | 'profile-menu' | 'profile-external' | 'profile-from-messages') => void;
  showAccessRightsModal?: string | null;
  onSetShowAccessRightsModal?: (listId: string | null) => void;
  onActiveTabChange?: (tab: ProfileTab) => void;
  drafts?: DraftItem[];
  onResumeDraft?: (draft: DraftItem) => void;
  onDeleteTopicDraft?: (draftId?: string) => void;
  onDeleteCommentDraft?: (topicId: string) => void;
}> = ({ currentUser, profileUserId, onMessageUser, onOpenMessages, initialTab, topics = [], drafts = [], onTopicSelect, onCommentSelect, onCommentSelectById, onCloseProfile, userMenuEntry = 'profile-external', onSetProfileUserId, onSetUserMenuEntry, showAccessRightsModal, onSetShowAccessRightsModal, onActiveTabChange, onResumeDraft, onDeleteTopicDraft, onDeleteCommentDraft }) => {
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab ?? 'topics');
  const lastInitialTabRef = useRef<ProfileTab | undefined>(initialTab);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [moderatorActivities, setModeratorActivities] = useState<any[]>([]);
  const [moderatorActivitiesLoading, setModeratorActivitiesLoading] = useState(false);
  // Saves state
  const [saveLists, setSaveLists] = useState<Array<SaveList & { saves: Save[] }>>([]);
  const [unlistedSaves, setUnlistedSaves] = useState<Save[]>([]);
  const [savesLoading, setSavesLoading] = useState(false);
  const [savesCount, setSavesCount] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showRenameModal, setShowRenameModal] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingReports, setPendingReports] = useState(0);
  const [followPending, setFollowPending] = useState(false);
  const { notify } = useNotifications();
  const [loadingProfileTopicId, setLoadingProfileTopicId] = useState<string | null>(null);
  const [loadingProfileCommentId, setLoadingProfileCommentId] = useState<string | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [sectionVisibilitySaving, setSectionVisibilitySaving] = useState<'topics' | 'comments' | null>(null);
  const [topicVisibilitySaving, setTopicVisibilitySaving] = useState<Record<string, boolean>>({});
  const [commentVisibilitySaving, setCommentVisibilitySaving] = useState<Record<string, boolean>>({});
  const [topicsPrivacyFilter, setTopicsPrivacyFilter] = useState<'public' | 'private'>('public');
  const [commentsPrivacyFilter, setCommentsPrivacyFilter] = useState<'public' | 'private'>('public');

  // Determine which user profile to show
  const targetUserId = profileUserId || currentUser?.id;
  const isOwnProfile = !profileUserId || profileUserId === currentUser?.id;
  const canViewNotifications = isOwnProfile;
  const canViewSaves = true; // Everyone can see saved section (but only public lists for others)
  const canViewDrafts = isOwnProfile;
  const draftCount = drafts.length;
  // Only show Moderation tab on your own profile
  const canViewModeration = isOwnProfile;
  const isModerator = currentUser?.role === 'MODERATOR';
  const profileUserIsModerator = profile?.user?.role === 'MODERATOR';

  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);

  useEffect(() => {
    setTopicsPrivacyFilter('public');
    setCommentsPrivacyFilter('public');
  }, [targetUserId]);

  const handleProfileTopicClick = async (topic: any) => {
    if (!onTopicSelect || loadingProfileTopicId === topic.id) return;

    // Save current profile state for back navigation
    sessionStorage.setItem('returnToProfile', JSON.stringify({
      userId: profileUserId || currentUser?.id,
      tab: 'topics',
      entry: userMenuEntry
    }));

    const fullTopic = topics.find((t: Topic) => t.id === topic.id);
    let targetTopic = fullTopic;
    let triggeredLoading = false;

    try {
      if (!targetTopic) {
        triggeredLoading = true;
        setLoadingProfileTopicId(topic.id);
        const fetched = await topicsApi.getTopic(topic.id);
        targetTopic = fetched;
      }

      if (targetTopic) {
        onTopicSelect(targetTopic);
        onCommentSelect?.(null);
        onCloseProfile?.();
      } else {
        notify({ type: 'warning', message: 'Unable to load this topic right now.' });
      }
    } catch (err) {
      console.error('Failed to load topic from profile list:', err);
      notify({ type: 'error', message: 'Failed to open this topic.' });
    } finally {
      if (triggeredLoading) {
        setLoadingProfileTopicId((prev) => (prev === topic.id ? null : prev));
      }
    }
  };

  const handleProfileCommentClick = async (comment: any) => {
    if (!onTopicSelect || !onCommentSelect || loadingProfileCommentId === comment.id) return;

    // Save current profile state for back navigation
    sessionStorage.setItem('returnToProfile', JSON.stringify({
      userId: profileUserId || currentUser?.id,
      tab: 'comments',
      entry: userMenuEntry
    }));

    const fullTopic = topics.find((t: Topic) => t.id === comment.topicId);
    let targetTopic = fullTopic;
    let triggeredLoading = false;

    try {
      if (!targetTopic) {
        triggeredLoading = true;
        setLoadingProfileCommentId(comment.id);
        const fetched = await topicsApi.getTopic(comment.topicId);
        targetTopic = fetched;
      }

      if (targetTopic) {
        onTopicSelect(targetTopic, comment.id);
        // Let the App's useEffect handle comment selection via pendingSelectCommentId
        // Don't call onCommentSelect immediately as it conflicts with topic switching
        // Delay the onCommentSelectById call to ensure topic switching is complete
        setTimeout(() => {
          onCommentSelectById?.(comment.id);
        }, 100);
        onCloseProfile?.();
      } else {
        notify({ type: 'warning', message: 'Unable to load this comment right now.' });
      }
    } catch (err) {
      console.error('Failed to load comment from profile list:', err);
      notify({ type: 'error', message: 'Failed to open this comment.' });
    } finally {
      if (triggeredLoading) {
        setLoadingProfileCommentId((prev) => (prev === comment.id ? null : prev));
      }
    }
  };

  // Handle clicking on saved topics (similar to handleProfileTopicClick)
  const handleSavedTopicClick = async (save: Save) => {
    if (!save.topic || !onTopicSelect || loadingProfileTopicId === save.topic.id) return;

    // Save current profile state for back navigation
    sessionStorage.setItem('returnToProfile', JSON.stringify({
      userId: profileUserId || currentUser?.id,
      tab: 'saved',
      entry: userMenuEntry
    }));

    const fullTopic = topics.find((t: Topic) => t.id === save.topic!.id);
    let targetTopic = fullTopic;
    let triggeredLoading = false;

    try {
      if (!targetTopic) {
        // Topic not in current list, fetch it
        triggeredLoading = true;
        setLoadingProfileTopicId(save.topic.id);
        const fetched = await topicsApi.getTopic(save.topic.id);
        targetTopic = fetched;
      }
      if (targetTopic) {
        onTopicSelect(targetTopic);
        onCommentSelect?.(null);
        onCloseProfile?.();
      } else {
        notify({ type: 'warning', message: 'Unable to load this topic right now.' });
      }
    } catch (err) {
      console.error('Failed to load topic from saved item:', err);
      notify({ type: 'error', message: 'Failed to open this topic.' });
    } finally {
      if (triggeredLoading) {
        setLoadingProfileTopicId((prev) => (prev === save.topic!.id ? null : prev));
      }
    }
  };

  // Handle clicking on saved comments (similar to handleProfileCommentClick)
  const handleSavedCommentClick = async (save: Save) => {
    if (!save.comment || !onTopicSelect || !onCommentSelect || loadingProfileCommentId === save.comment.id) return;

    // Save current profile state for back navigation
    sessionStorage.setItem('returnToProfile', JSON.stringify({
      userId: profileUserId || currentUser?.id,
      tab: 'saved',
      entry: userMenuEntry
    }));

    const fullTopic = topics.find((t: Topic) => t.id === save.comment!.topic.id);
    let targetTopic = fullTopic;
    let triggeredLoading = false;

    try {
      if (!targetTopic) {
        // Topic not in current list, fetch it
        triggeredLoading = true;
        setLoadingProfileCommentId(save.comment!.id);
        const fetched = await topicsApi.getTopic(save.comment!.topic.id);
        targetTopic = fetched;
      }
      if (targetTopic) {
        onTopicSelect(targetTopic, save.comment!.id);
        // Let the App's useEffect handle comment selection via pendingSelectCommentId
        // Don't call onCommentSelect immediately as it conflicts with topic switching
        // Delay the onCommentSelectById call to ensure topic switching is complete
        setTimeout(() => {
          onCommentSelectById?.(save.comment!.id);
        }, 100);
        onCloseProfile?.();
      } else {
        notify({ type: 'warning', message: 'Unable to load this comment right now.' });
      }
    } catch (err) {
      console.error('Failed to load topic/comment from saved item:', err);
      notify({ type: 'error', message: 'Failed to open this comment.' });
    } finally {
      if (triggeredLoading) {
        setLoadingProfileCommentId((prev) => (prev === save.comment!.id ? null : prev));
      }
    }
  };

  const toggleSectionVisibility = async (section: 'topics' | 'comments') => {
    if (!isOwnProfile || !profile || !targetUserId) return;
    const nextValue = section === 'topics'
      ? !profile.showTopicsInProfile
      : !profile.showCommentsInProfile;

    const sectionName = section === 'topics' ? 'topics' : 'comments';
    const itemCount = section === 'topics' ? profile.recentTopics.length : profile.recentComments.length;

    // Show confirmation dialog
    const confirmMessage = nextValue
      ? `Show all ${sectionName} on your profile?\n\nThis will mark each of your ${itemCount} ${sectionName} as visible to visitors, overriding any individual visibility settings.\n\nYou can still hide specific ${sectionName} afterward.`
      : `Hide all ${sectionName} from your profile?\n\nThis will hide each of your ${itemCount} ${sectionName} from visitors and override their individual visibility settings.\n\nYou can show them again later or adjust individual ${sectionName} afterward.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setSectionVisibilitySaving(section);
      const payload = section === 'topics'
        ? { showTopicsInProfile: nextValue, applyToTopics: true }
        : { showCommentsInProfile: nextValue, applyToComments: true };
      const updated = await usersApi.updateProfileVisibility(targetUserId, payload);
      setProfile((prev: any) => {
        if (!prev) return prev;
        const base = {
          ...prev,
          showTopicsInProfile: updated.showTopicsInProfile,
          showCommentsInProfile: updated.showCommentsInProfile
        };
        if (section === 'topics') {
          const nextTopics = prev.recentTopics.map((topic: Topic) => ({
            ...topic,
            showInProfile: nextValue
          }));
          return {
            ...base,
            recentTopics: nextTopics,
            topicCount: nextValue ? nextTopics.length : 0
          };
        }
        const nextComments = prev.recentComments.map((comment: Comment) => ({
          ...comment,
          showInProfile: nextValue
        }));
        return {
          ...base,
          recentComments: nextComments,
          commentCount: nextValue ? nextComments.length : 0
        };
      });
      notify({
        type: 'success',
        message: section === 'topics'
          ? (nextValue ? 'All topics are now visible on your profile.' : 'All topics are now hidden from your profile.')
          : (nextValue ? 'All comments are now visible on your profile.' : 'All comments are now hidden from your profile.'),
        persistent: false
      });
    } catch (err: any) {
      console.error('Failed to update profile visibility:', err);
      notify({
        type: 'error',
        message: err.response?.data?.error || 'Failed to update profile visibility.',
        persistent: false
      });
    } finally {
      setSectionVisibilitySaving(null);
    }
  };

  const handleTopicVisibilityToggle = async (topicId: string, currentlyVisible: boolean) => {
    if (!isOwnProfile || !targetUserId) return;
    const nextValue = !currentlyVisible;

    try {
      setTopicVisibilitySaving(prev => ({ ...prev, [topicId]: true }));
      await usersApi.updateTopicProfileVisibility(targetUserId, topicId, nextValue);
      setProfile((prev: any) => {
        if (!prev) return prev;
        const recentTopics = prev.recentTopics.map((topic: Topic) =>
          topic.id === topicId ? { ...topic, showInProfile: nextValue } : topic
        );
        const visibleCount = recentTopics.filter((topic: Topic) => topic.showInProfile !== false).length;
        return {
          ...prev,
          recentTopics,
          topicCount: visibleCount,
          showTopicsInProfile: visibleCount > 0
        };
      });
      notify({
        type: 'success',
        message: nextValue ? 'Topic will now appear on your profile.' : 'Topic hidden from your profile.',
        persistent: false
      });
    } catch (err: any) {
      console.error('Failed to update topic visibility:', err);
      notify({
        type: 'error',
        message: err.response?.data?.error || 'Failed to update topic visibility.',
        persistent: false
      });
    } finally {
      setTopicVisibilitySaving(prev => {
        const next = { ...prev };
        delete next[topicId];
        return next;
      });
    }
  };

  const handleCommentVisibilityToggle = async (commentId: string, currentlyVisible: boolean) => {
    if (!isOwnProfile || !targetUserId) return;
    const nextValue = !currentlyVisible;

    try {
      setCommentVisibilitySaving(prev => ({ ...prev, [commentId]: true }));
      await usersApi.updateCommentProfileVisibility(targetUserId, commentId, nextValue);
      setProfile((prev: any) => {
        if (!prev) return prev;
        const recentComments = prev.recentComments.map((comment: Comment) =>
          comment.id === commentId ? { ...comment, showInProfile: nextValue } : comment
        );
        const visibleCount = recentComments.filter((comment: Comment) => comment.showInProfile !== false).length;
        return {
          ...prev,
          recentComments,
          commentCount: visibleCount,
          showCommentsInProfile: visibleCount > 0
        };
      });
      notify({
        type: 'success',
        message: nextValue ? 'Comment will now appear on your profile.' : 'Comment hidden from your profile.',
        persistent: false
      });
    } catch (err: any) {
      console.error('Failed to update comment visibility:', err);
      notify({
        type: 'error',
        message: err.response?.data?.error || 'Failed to update comment visibility.',
        persistent: false
      });
    } finally {
      setCommentVisibilitySaving(prev => {
        const next = { ...prev };
        delete next[commentId];
        return next;
      });
    }
  };

  const handleToggleFollow = async () => {
    if (!targetUserId) return;

    if (!currentUser?.id) {
      try {
        const dest = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
        window.location.href = dest;
      } catch { }
      return;
    }

    if (!profile) return;

    try {
      setFollowPending(true);
      if (profile.isFollowing) {
        const result = await usersApi.unfollowUser(targetUserId);
        setProfile((prev: any) => prev ? { ...prev, isFollowing: result.isFollowing, followerCount: result.followerCount } : prev);
        notify({ type: 'info', message: 'Unfollowed user.', persistent: false });
      } else {
        const result = await usersApi.followUser(targetUserId);
        setProfile((prev: any) => prev ? { ...prev, isFollowing: result.isFollowing, followerCount: result.followerCount } : prev);
        notify({ type: 'success', message: 'You are now following this user.', persistent: false });
      }
    } catch (err) {
      console.error('Failed to toggle follow:', err);
      notify({ type: 'error', message: 'Could not update follow status.' });
    } finally {
      setFollowPending(false);
    }
  };

  // Helper function to check if a saved item should be visible to the current viewer
  const canViewSavedItem = (save: Save): boolean => {
    if (!save) return false;
    if (save.contentType === 'TOPIC') {
      const topicAny: any = save.topic;
      if (!topicAny) return false;
      if (topicAny.isDeleted) return false;
      if (topicAny.deletedBy) return false;
      if (typeof topicAny.moderationReason === 'string' && topicAny.moderationReason.trim().length > 0) {
        return false;
      }
      if (topicAny.user?.isDeleted) return false;
      return true;
    }
    if (save.contentType === 'COMMENT') {
      const commentAny: any = save.comment;
      if (!commentAny) return false;
      if (commentAny.user?.isDeleted) return false;
      const trimmed = (commentAny.content || '').trim().toLowerCase();
      if (!trimmed || trimmed === '[deleted]' || trimmed === '[removed]') return false;
      if (commentAny.isDeleted) return false;
      if (commentAny.deletedBy) return false;
      if (typeof commentAny.moderationReason === 'string' && commentAny.moderationReason.trim().length > 0) {
        return false;
      }
      const topicAny: any = commentAny.topic;
      if (!topicAny) return false;
      if (topicAny.isDeleted) return false;
      if (topicAny.deletedBy) return false;
      if (topicAny.user?.isDeleted) return false;
      if (typeof topicAny.moderationReason === 'string' && topicAny.moderationReason.trim().length > 0) {
        return false;
      }
      return true;
    }
    return false;
  };

  // Handler to remove a saved item
  const handleRemoveSave = async (saveId: string, itemName: string) => {
    const confirmed = window.confirm(`Remove "${itemName}" from your saved items?`);
    if (!confirmed) return;

    try {
      await savesApi.removeSave(saveId);
      // Update local state to remove the save
      setUnlistedSaves(prev => prev.filter(s => s.id !== saveId));
      setSaveLists(prev => prev.map(list => ({
        ...list,
        saves: list.saves?.filter(s => s.id !== saveId) || []
      })));
      // Update count
      setSavesCount(prev => Math.max(0, prev - 1));
      notify({ type: 'success', message: 'Item removed from saves', persistent: false });
    } catch (err) {
      console.error('Failed to remove save:', err);
      notify({ type: 'error', message: 'Failed to remove item' });
    }
  };

  // Handler functions for list management
  const handleShareList = (listId: string, listName: string) => {
    const url = `${window.location.origin}/?saveList=${listId}`;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => notify({
          type: 'success',
          message: `Link to "${listName}" copied to clipboard`,
          persistent: false
        }))
        .catch(() => notify({
          type: 'info',
          message: `Share link: ${url}`,
          persistent: false
        }));
    } else {
      notify({
        type: 'info',
        message: `Share link: ${url}`,
        persistent: false
      });
    }
  };

  const handleTogglePin = async (listId: string) => {
    try {
      const updatedList = await savesApi.toggleListPin(listId);
      setSaveLists(prev => prev.map(list =>
        list.id === listId
          ? { ...list, isPinned: updatedList.isPinned }
          : list
      ).sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }));
      notify({
        type: 'success',
        message: updatedList.isPinned ? 'List pinned' : 'List unpinned',
        persistent: false
      });
    } catch (error: any) {
      notify({
        type: 'error',
        message: error.response?.data?.error || 'Failed to toggle pin status'
      });
    }
  };

  const handleDeleteList = async (listId: string) => {
    try {
      await savesApi.deleteList(listId);
      setSaveLists(prev => prev.filter(list => list.id !== listId));
      setShowDeleteConfirm(null);
      notify({ type: 'success', message: 'List deleted successfully', persistent: false });
    } catch (error: any) {
      notify({
        type: 'error',
        message: error.response?.data?.error || 'Failed to delete list'
      });
    }
  };

  const handleRenameList = async () => {
    if (!showRenameModal || !renameValue.trim()) return;

    try {
      const updatedList = await savesApi.updateList(showRenameModal, { name: renameValue.trim() });
      setSaveLists(prev => prev.map(list =>
        list.id === showRenameModal
          ? { ...list, name: updatedList.name }
          : list
      ));
      setShowRenameModal(null);
      setRenameValue('');
      notify({ type: 'success', message: 'List renamed successfully', persistent: false });
    } catch (error: any) {
      notify({
        type: 'error',
        message: error.response?.data?.error || 'Failed to rename list'
      });
    }
  };

  const handleUpdateAccessRights = async (listId: string, visibility: any, audienceUserIds: string[]) => {
    try {
      const legacy = savesApi.visibilityToLegacy(visibility, audienceUserIds);
      await savesApi.updateList(listId, legacy);
      setSaveLists(prev => prev.map(list =>
        list.id === listId
          ? { ...list, ...legacy }
          : list
      ));
      onSetShowAccessRightsModal?.(null);
      notify({ type: 'success', message: 'Access rights updated', persistent: false });
    } catch (error: any) {
      notify({
        type: 'error',
        message: error.response?.data?.error || 'Failed to update access rights'
      });
    }
  };

  useEffect(() => {
    if (!targetUserId) return;

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await usersApi.getProfile(targetUserId);

        const rawTopics = data.topics || [];
        const rawComments = data.comments || [];
        const publicTopicCount = rawTopics.filter((t: any) => t.showInProfile !== false).length;
        const publicCommentCount = rawComments.filter((c: any) => c.showInProfile !== false).length;
        const topicCount = isOwnProfile ? rawTopics.length : publicTopicCount;
        const commentCount = isOwnProfile ? rawComments.length : publicCommentCount;

        const topicVisibilityMap = new Map<string, TopicVisibility>();
        rawTopics.forEach((t: any) => {
          topicVisibilityMap.set(t.id, normalizeVisibility(t.visibility));
        });

        const profileUser = {
          id: data.id,
          email: data.email,
          username: data.username,
          reputation: data.reputation,
          role: data.role,
          createdAt: data.createdAt
        } as User;

        const recentTopics: Topic[] = [];
        (data.topics || []).forEach((t: any) => {
          recentTopics.push({
            id: t.id,
            userId: data.id,
            title: t.title,
            content: '',
            language: 'en',
            upvotes: t.upvotes || 0,
            downvotes: t.downvotes || 0,
            commentCount: t.commentCount || 0,
            createdAt: t.createdAt,
            visibility: (t.visibility || 'PUBLIC') as TopicVisibility,
            basemap: (t.basemap || 'osm') as BasemapId,
            user: profileUser,
            showInProfile: t.showInProfile !== false
          } as Topic);
        });

        const recentComments: Comment[] = [];
        (data.comments || []).forEach((c: any) => {
          recentComments.push({
            id: c.id,
            topicId: c.topicId,
            userId: data.id,
            content: c.content,
            upvotes: c.upvotes || 0,
            downvotes: c.downvotes || 0,
            createdAt: c.createdAt,
            user: profileUser,
            showInProfile: c.showInProfile !== false,
            topicVisibility: (c.topic?.visibility
              || topicVisibilityMap.get(c.topicId)
              || 'PRIVATE_SELF') as TopicVisibility
          } as Comment);
        });

        setProfile({
          user: data,
          topicCount,
          commentCount,
          totalUpvotes: (data.topics || []).reduce((acc: number, t: any) => acc + (t.upvotes || 0), 0) +
            (data.comments || []).reduce((acc: number, c: any) => acc + (c.upvotes || 0), 0),
          totalDownvotes: (data.topics || []).reduce((acc: number, t: any) => acc + (t.downvotes || 0), 0) +
            (data.comments || []).reduce((acc: number, c: any) => acc + (c.downvotes || 0), 0),
          joinDate: data.createdAt,
          recentTopics: recentTopics as Topic[],
          recentComments: recentComments as Comment[],
          moderationActions: data.moderationActions || [],
          followerCount: data.followerCount ?? 0,
          followingCount: data.followingCount ?? 0,
          isFollowing: data.isFollowing ?? false,
          isFollowedBy: data.isFollowedBy ?? false,
          showTopicsInProfile: data.showTopicsInProfile ?? true,
          showCommentsInProfile: data.showCommentsInProfile ?? true,
          allowSearchIndexing: data.allowSearchIndexing ?? true
        } as UserProfile);
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [targetUserId, currentUser, isOwnProfile]);

  // Fetch save count on profile load (for tab badge)
  useEffect(() => {
    if (!canViewSaves || !targetUserId) return;

    const fetchSaveCount = async () => {
      try {
        if (isOwnProfile) {
          const data = await savesApi.getAllUserSaves('newest');
          const totalCount = data.unlistedSaves.length
            + data.lists.reduce((acc, list) => acc + (list.saves?.length || 0), 0);
          setSavesCount(totalCount);
        } else {
          const data = await savesApi.getUserPublicLists(targetUserId);
          const listsWithSaves = await Promise.all(
            data.lists.map(async (list) => {
              try {
                const listData = await savesApi.getList(list.id, 'newest');
                return Array.isArray(listData.saves) ? listData.saves.filter(canViewSavedItem) : [];
              } catch {
                return [];
              }
            })
          );
          const totalCount = listsWithSaves.reduce((acc, saves) => acc + saves.length, 0);
          setSavesCount(totalCount);
        }
      } catch (err) {
        console.error('Failed to fetch save count:', err);
        setSavesCount(0);
      }
    };

    fetchSaveCount();
  }, [targetUserId, canViewSaves, isOwnProfile]);

  // Fetch saves when saved tab is active
  useEffect(() => {
    if (!canViewSaves || activeTab !== 'saved') return;

    const fetchSaves = async () => {
      try {
        setSavesLoading(true);

        if (isOwnProfile) {
          // For own profile, fetch all saves including private lists
          const data = await savesApi.getAllUserSaves('newest');
          setSaveLists(data.lists);
          setUnlistedSaves(data.unlistedSaves);
          // Update count with actual data
          const totalCount = data.unlistedSaves.length + data.lists.reduce((acc, list) => acc + (list.saves?.length || 0), 0);
          setSavesCount(totalCount);
        } else {
          // For other users' profiles, fetch only public lists
          const data = await savesApi.getUserPublicLists(targetUserId!);
          // Fetch each list's items
          const listsWithSaves = await Promise.all(
            data.lists.map(async (list) => {
              try {
                const listData = await savesApi.getList(list.id, 'newest');
                return {
                  ...list,
                  saves: listData.saves || []
                };
              } catch {
                return { ...list, saves: [] };
              }
            })
          );
          setSaveLists(listsWithSaves);
          setUnlistedSaves([]);
          // Update count with actual data
          const totalCount = listsWithSaves.reduce((acc, list) => acc + (list.saves?.length || 0), 0);
          setSavesCount(totalCount);
        }
      } catch (err) {
        console.error('Failed to fetch saves:', err);
      } finally {
        setSavesLoading(false);
      }
    };

    fetchSaves();
  }, [activeTab, canViewSaves, isOwnProfile, targetUserId]);

  // Honor initialTab prop (e.g., open Notifications directly) without fighting user interaction
  useEffect(() => {
    if (!initialTab) {
      if (lastInitialTabRef.current) {
        lastInitialTabRef.current = undefined;
      }
      return;
    }
    if (lastInitialTabRef.current === initialTab) return;
    lastInitialTabRef.current = initialTab;
    setActiveTab(initialTab);
  }, [initialTab]);

  // Load notifications when notifications tab is active
  useEffect(() => {
    if (activeTab !== 'notifications' || !isOwnProfile) return;

    const fetchNotifications = async () => {
      try {
        setNotificationsLoading(true);
        const data = await notificationsApi.get();
        setNotifications(data.notifications || []);
      } catch (err) {
        console.error('Failed to load notifications:', err);
      } finally {
        setNotificationsLoading(false);
      }
    };

    fetchNotifications();
  }, [activeTab, isOwnProfile]);

  // Fetch moderator activities when mod-activities tab is active
  useEffect(() => {
    if (activeTab !== 'mod-activities' || !profileUserIsModerator || !targetUserId || !isOwnProfile) return;

    const fetchModeratorActivities = async () => {
      try {
        setModeratorActivitiesLoading(true);
        const data = await usersApi.getModeratorActivities(targetUserId);
        setModeratorActivities(data.moderationActivities || []);
      } catch (err) {
        console.error('Failed to fetch moderator activities:', err);
      } finally {
        setModeratorActivitiesLoading(false);
      }
    };

    fetchModeratorActivities();
  }, [activeTab, profileUserIsModerator, targetUserId, isOwnProfile]);

  // If viewing someone else's profile, prevent showing Moderation-related tabs
  useEffect(() => {
    if (!isOwnProfile && (activeTab === 'mod-activities' || activeTab === 'moderation' || activeTab === 'drafts')) {
      setActiveTab('topics');
    }
  }, [isOwnProfile, activeTab]);

  // Fetch pending reports for moderators to highlight the tab
  useEffect(() => {
    if (!isModerator || !isOwnProfile) {
      setPendingReports(0);
      return;
    }

    const fetchPendingReports = async () => {
      try {
        const r = await adminApi.getReports();
        setPendingReports(Array.isArray(r.data) ? r.data.length : 0);
      } catch (err) {
        console.error('Failed to fetch pending reports:', err);
        setPendingReports(0);
      }
    };

    fetchPendingReports();
    const interval = setInterval(fetchPendingReports, 30000);

    return () => clearInterval(interval);
  }, [isModerator, isOwnProfile]);

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      // Also update global provider badge immediately
      try { (window as any).dispatchEvent?.(new Event('notifications:markAllRead')); } catch { }
      notify({ type: 'success', message: 'All notifications marked as read', persistent: false });
    } catch (err) {
      notify({ type: 'error', message: 'Failed to mark notifications as read' });
    }
  };

  // Notification messages can embed [user:id]name[/user] and [topic:id]title[/topic]
  // markup; both render as clickable links.
  const NOTIFICATION_MARKUP_REGEX = /\[(user|topic):([^\]]+)\](.+?)\[\/\1\]/g;

  const handleNotificationUserClick = (userId: string) => {
    if (!userId || !onSetProfileUserId) return;
    if (userId === (profileUserId || currentUser?.id)) return;

    try {
      sessionStorage.setItem('returnToProfile', JSON.stringify({
        userId: profileUserId || currentUser?.id,
        tab: activeTab,
        entry: userMenuEntry
      }));
    } catch { }

    onSetUserMenuEntry?.('profile-external');
    onSetProfileUserId(userId);
    setActiveTab('topics');
    setProfile(null);
    setLoading(true);
  };

  const handleNotificationTopicClick = (topicId: string) => {
    if (!topicId) return;
    // Full navigation; App resolves /t/:topicId deep links on load
    window.location.href = `/t/${encodeURIComponent(topicId)}`;
  };

  const renderNotificationMessage = (notification: any): React.ReactNode => {
    const message: string = notification?.message || '';
    const linkStyle: React.CSSProperties = {
      border: 'none',
      background: 'transparent',
      color: '#2563eb',
      cursor: 'pointer',
      padding: 0,
      fontSize: 'inherit',
      textDecoration: 'underline'
    };

    const nodes: React.ReactNode[] = [];
    const regex = new RegExp(NOTIFICATION_MARKUP_REGEX.source, 'g');
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(message)) !== null) {
      const [fullMatch, kind, id, label] = match;
      if (match.index > lastIndex) {
        nodes.push(<span key={`text-${lastIndex}`}>{message.slice(lastIndex, match.index)}</span>);
      }
      nodes.push(
        <button
          key={`${kind}-${id}-${match.index}`}
          type="button"
          onClick={() => (kind === 'user' ? handleNotificationUserClick(id) : handleNotificationTopicClick(id))}
          style={linkStyle}
        >
          {label}
        </button>
      );
      lastIndex = match.index + fullMatch.length;
    }

    if (nodes.length === 0) {
      return message;
    }
    if (lastIndex < message.length) {
      nodes.push(<span key={`text-${lastIndex}`}>{message.slice(lastIndex)}</span>);
    }

    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
        {nodes}
      </span>
    );
  };

  const [savingSearchIndexing, setSavingSearchIndexing] = useState(false);

  const toggleSearchIndexing = async () => {
    if (!isOwnProfile || !profile || !targetUserId) return;
    const nextValue = !(profile.allowSearchIndexing ?? true);
    try {
      setSavingSearchIndexing(true);
      const updated = await usersApi.updateProfileVisibility(targetUserId, { allowSearchIndexing: nextValue });
      setProfile((prev: any) => prev ? { ...prev, allowSearchIndexing: updated.allowSearchIndexing } : prev);
      notify({
        type: 'success',
        message: updated.allowSearchIndexing
          ? 'Search engines may index your topics and comments again.'
          : 'Your topics and comments will be hidden from search engines. Already-indexed pages can take a while to disappear.'
      });
    } catch (err) {
      notify({ type: 'error', message: 'Failed to update search engine visibility' });
    } finally {
      setSavingSearchIndexing(false);
    }
  };

  const renderSearchIndexingControl = () => {
    if (!isOwnProfile || !profile) return null;
    const allowed = profile.allowSearchIndexing ?? true;
    return (
      <div
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px'
        }}
      >
        <div>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>Search engine visibility</p>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
            {allowed
              ? 'Google and other search engines may index your topics and your comments on public topic pages.'
              : 'Your topic pages are marked noindex and your comments are left out of public topic pages served to search engines.'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={toggleSearchIndexing}
          disabled={savingSearchIndexing}
          style={{ whiteSpace: 'nowrap' }}
        >
          {savingSearchIndexing ? 'Saving…' : allowed ? 'Hide from search engines' : 'Allow indexing'}
        </button>
      </div>
    );
  };

  const renderSectionVisibilityControls = (section: 'topics' | 'comments') => {
    if (!isOwnProfile || !profile) return null;
    const isVisible = section === 'topics' ? profile.showTopicsInProfile : profile.showCommentsInProfile;
    const saving = sectionVisibilitySaving === section;
    const sectionName = section === 'topics' ? 'topics' : 'comments';
    const title = 'Section visibility';
    const description = isVisible
      ? `All ${sectionName} are currently visible to visitors. Use this control to hide every ${sectionName} at once.`
      : `All ${sectionName} are currently hidden from visitors. Use this control to show every ${sectionName} at once.`;

    return (
      <div
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px'
        }}
      >
        <div>
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>{title}</p>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>{description}</p>
        </div>
        <button
          type="button"
          onClick={() => toggleSectionVisibility(section)}
          disabled={saving}
          style={{
            borderRadius: '999px',
            border: '2px solid #111827',
            padding: '6px 14px',
            fontSize: '13px',
            fontWeight: 600,
            minWidth: '90px',
            backgroundColor: isVisible ? '#ecfdf5' : '#fef2f2',
            color: isVisible ? '#047857' : '#b91c1c',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving…' : (isVisible ? `Hide all ${sectionName}` : `Show all ${sectionName}`)}
        </button>
      </div>
    );
  };

  const renderPrivacyToggle = (section: 'topics' | 'comments') => {
    if (!isOwnProfile || !profile) return null;

    const isTopicSection = section === 'topics';
    const items = (isTopicSection ? profile.recentTopics : profile.recentComments) ?? [];
    const publicCount = isTopicSection
      ? (items as Topic[]).filter(isPublicTopic).length
      : (items as Comment[]).filter(isPublicComment).length;
    const privateCount = items.length - publicCount;
    const activeFilter = isTopicSection ? topicsPrivacyFilter : commentsPrivacyFilter;
    const setFilter = isTopicSection ? setTopicsPrivacyFilter : setCommentsPrivacyFilter;

    const baseButtonStyles = {
      border: 'none',
      padding: '6px 14px',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer' as const,
      background: 'transparent',
      color: '#4b5563'
    };

    return (
      <div
        style={{
          display: 'inline-flex',
          border: '1px solid #e5e7eb',
          borderRadius: '999px',
          overflow: 'hidden',
          marginBottom: '12px'
        }}
      >
        <button
          type='button'
          onClick={() => setFilter('public')}
          style={{
            ...baseButtonStyles,
            backgroundColor: activeFilter === 'public' ? '#111827' : 'transparent',
            color: activeFilter === 'public' ? '#ffffff' : '#4b5563'
          }}
        >
          Public ({publicCount})
        </button>
        <button
          type='button'
          onClick={() => setFilter('private')}
          style={{
            ...baseButtonStyles,
            borderLeft: '1px solid #e5e7eb',
            backgroundColor: activeFilter === 'private' ? '#111827' : 'transparent',
            color: activeFilter === 'private' ? '#ffffff' : '#4b5563'
          }}
        >
          Private ({privateCount})
        </button>
      </div>
    );
  };

  const renderTopics = () => {
    if (!profile) return null;

    if (!isOwnProfile && !profile.showTopicsInProfile) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          This user keeps their topics hidden from their profile.
        </div>
      );
    }

    const allTopics = profile.recentTopics || [];
    const ownerPublicTopics = allTopics.filter(isPublicTopic);
    const ownerPrivateTopics = allTopics.filter((topic: Topic) => !isPublicTopic(topic));
    const showingPrivate = isOwnProfile && topicsPrivacyFilter === 'private';
    const ownerTopics = showingPrivate ? ownerPrivateTopics : ownerPublicTopics;
    const visitorTopics = allTopics.filter((topic: Topic) => topic.showInProfile !== false);
    const visibleTopics = isOwnProfile ? ownerTopics : visitorTopics;
    const emptyStateMessage = isOwnProfile
      ? (showingPrivate ? 'No non-public topics yet.' : 'No public topics yet.')
      : 'No public topics to show.';

    return (
      <div style={{ padding: '16px' }}>
        {renderPrivacyToggle('topics')}
        {renderSearchIndexingControl()}
        {renderSectionVisibilityControls('topics')}
        {!visibleTopics.length ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px', margin: 0 }}>
            {emptyStateMessage}
          </p>
        ) : (
          visibleTopics.map((topic: any) => (
            <div
              key={topic.id}
              onClick={() => handleProfileTopicClick(topic)}
              style={{
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginBottom: '12px',
                cursor: loadingProfileTopicId === topic.id ? 'wait' : 'pointer',
                transition: 'background-color 0.2s, border-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '600' }}>
                {topic.title}
              </h3>
              <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#6b7280' }}>
                <span>↑ {topic.upvotes}</span>
                <span>↓ {topic.downvotes}</span>
                <span>{topic.commentCount} comments</span>
                <span>{new Date(topic.createdAt).toLocaleDateString()}</span>
              </div>
              {loadingProfileTopicId === topic.id && (
                <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                  Loading topic…
                </div>
              )}
              {isOwnProfile && (
                <div style={{
                  marginTop: '12px',
                  borderTop: '1px dashed #e5e7eb',
                  paddingTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  {showingPrivate ? (
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      padding: '4px 10px',
                      borderRadius: '999px',
                      backgroundColor: '#e0e7ff',
                      color: '#312e81'
                    }}>
                      Only visible to you
                    </span>
                  ) : (
                    <>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: '999px',
                        backgroundColor: topic.showInProfile === false ? '#fee2e2' : '#d1fae5',
                        color: topic.showInProfile === false ? '#b91c1c' : '#065f46'
                      }}>
                        {topic.showInProfile === false ? 'Hidden from profile' : 'Visible on profile'}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTopicVisibilityToggle(topic.id, topic.showInProfile !== false);
                        }}
                        disabled={Boolean(topicVisibilitySaving[topic.id])}
                        style={{
                          borderRadius: '999px',
                          border: '2px solid #111827',
                          padding: '6px 14px',
                          fontSize: '12px',
                          fontWeight: 600,
                          backgroundColor: '#111827',
                          color: 'white',
                          cursor: topicVisibilitySaving[topic.id] ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {topicVisibilitySaving[topic.id] ? 'Saving…' : (topic.showInProfile === false ? 'Show' : 'Hide')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  const renderComments = () => {
    if (!profile) return null;

    if (!isOwnProfile && !profile.showCommentsInProfile) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          This user keeps their comments hidden from their profile.
        </div>
      );
    }

    const allComments = profile.recentComments || [];
    const ownerPublicComments = allComments.filter(isPublicComment);
    const ownerPrivateComments = allComments.filter((comment: Comment) => !isPublicComment(comment));
    const showingPrivate = isOwnProfile && commentsPrivacyFilter === 'private';
    const ownerComments = showingPrivate ? ownerPrivateComments : ownerPublicComments;
    const visitorComments = allComments.filter((comment: Comment) => comment.showInProfile !== false);
    const visibleComments = isOwnProfile ? ownerComments : visitorComments;
    const emptyStateMessage = isOwnProfile
      ? (showingPrivate ? 'No non-public comments yet.' : 'No public comments yet.')
      : 'No public comments to show.';

    return (
      <div style={{ padding: '16px' }}>
        {renderPrivacyToggle('comments')}
        {renderSectionVisibilityControls('comments')}
        {!visibleComments.length ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px', margin: 0 }}>
            {emptyStateMessage}
          </p>
        ) : (
          visibleComments.map((comment: any) => {
            const isExpanded = expandedComments.has(comment.id);
            const shouldTruncate = comment.content.length > 200;
            const displayText = isExpanded || !shouldTruncate ? comment.content : comment.content.slice(0, 200) + '...';

            return (
              <div
                key={comment.id}
                style={{
                  padding: '16px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  marginBottom: '12px',
                  transition: 'background-color 0.2s, border-color 0.2s'
                }}
              >
                <div
                  onClick={() => handleProfileCommentClick(comment)}
                  style={{
                    cursor: loadingProfileCommentId === comment.id ? 'wait' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.parentElement!.style.backgroundColor = '#f9fafb';
                    e.currentTarget.parentElement!.style.borderColor = '#3b82f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.parentElement!.style.backgroundColor = 'white';
                    e.currentTarget.parentElement!.style.borderColor = '#e5e7eb';
                  }}
                >
                  <LinkifiedText
                    as="p"
                    text={displayText}
                    style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: '1.5' }}
                  />
                  <div style={{ display: 'flex', gap: '16px', fontSize: '14px', color: '#6b7280' }}>
                    <span>↑ {comment.upvotes}</span>
                    <span>↓ {comment.downvotes}</span>
                    <span>{new Date(comment.createdAt).toLocaleDateString()}</span>
                  </div>
                  {loadingProfileCommentId === comment.id && (
                    <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                      Loading comment…
                    </div>
                  )}
                </div>
                {shouldTruncate && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedComments(prev => {
                        const next = new Set(prev);
                        if (isExpanded) {
                          next.delete(comment.id);
                        } else {
                          next.add(comment.id);
                        }
                        return next;
                      });
                    }}
                    style={{
                      marginTop: '8px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      color: '#3b82f6',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline'
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
                {isOwnProfile && (
                  <div style={{
                    marginTop: '12px',
                    borderTop: '1px dashed #e5e7eb',
                    paddingTop: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}>
                    {showingPrivate ? (
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '4px 10px',
                        borderRadius: '999px',
                        backgroundColor: '#e0e7ff',
                        color: '#312e81'
                      }}>
                        Only visible to you
                      </span>
                    ) : (
                      <>
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          padding: '4px 10px',
                          borderRadius: '999px',
                          backgroundColor: comment.showInProfile === false ? '#fee2e2' : '#d1fae5',
                          color: comment.showInProfile === false ? '#b91c1c' : '#065f46'
                        }}>
                          {comment.showInProfile === false ? 'Hidden from profile' : 'Visible on profile'}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCommentVisibilityToggle(comment.id, comment.showInProfile !== false);
                          }}
                          disabled={Boolean(commentVisibilitySaving[comment.id])}
                          style={{
                            borderRadius: '999px',
                            border: '2px solid #111827',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: 600,
                            backgroundColor: '#111827',
                            color: 'white',
                            cursor: commentVisibilitySaving[comment.id] ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {commentVisibilitySaving[comment.id] ? 'Saving…' : (comment.showInProfile === false ? 'Show' : 'Hide')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderNotifications = () => {
    if (notificationsLoading) {
      return <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>Loading notifications...</p>;
    }

    const hasUnread = notifications?.some(n => !n.read);

    return (
      <div style={{ padding: '16px' }}>
        {hasUnread && notifications?.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={handleMarkAllAsRead}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Mark all as read
            </button>
          </div>
        )}

        {!notifications?.length ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>No notifications yet</p>
        ) : (
          <div>
            {notifications.map((notification) => (
              <div
                key={notification.id}
                style={{
                  padding: '16px',
                  backgroundColor: notification.read ? 'white' : '#fef3c7',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  marginBottom: '12px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#374151',
                    textTransform: 'uppercase'
                  }}>
                    {notification.type.replace('_', ' ')}
                  </span>
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>
                    {new Date(notification.createdAt).toLocaleDateString()}
                  </span>
                </div>
                {notification.title && (
                  <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: '600' }}>
                    {notification.title}
                  </h4>
                )}
                <p style={{ margin: '0', fontSize: '14px', color: '#374151' }}>
                  {renderNotificationMessage(notification)}
                </p>
                {!notification.read && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '9999px',
                      fontSize: '11px',
                      padding: '2px 6px'
                    }}>
                      New
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderModeration = () => {
    if (!profile?.moderationActions?.length) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
          <p>No moderation history found.</p>
          <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
            This section shows when your content has been moderated or when moderation actions have affected your account.
          </p>
        </div>
      );
    }

    return (
      <div style={{ padding: '16px' }}>
        {profile.moderationActions.map((action: any) => {
          const typeLabel = (action.contentType || action.targetType || 'CONTENT').toString().toUpperCase();
          const when = new Date(action.createdAt).toLocaleDateString();
          const preview = action.contentPreview || action.preview || '';
          const id = action.contentId || action.targetId || 'unknown';
          const openHref = action.contentType === 'TOPIC'
            ? `/t/${id}`
            : '';

          return (
            <div key={action.id} style={{
              padding: '16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              marginBottom: '12px',
              borderLeft: '3px solid #dc2626'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', textTransform: 'uppercase', backgroundColor: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>
                    {action.actionType || 'DELETE'}
                  </span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{typeLabel}</span>
                  {preview && <span style={{ fontSize: 12, color: '#6b7280' }}>— "{preview}"</span>}
                  {id && id !== 'unknown' && action.actionType !== 'DELETE' && (
                    <a href={openHref} style={{ marginLeft: 8, fontSize: 12 }} className="btn btn-ghost">Open</a>
                  )}
                </div>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{when}</span>
              </div>

              <div style={{ fontSize: 14, color: '#374151', marginBottom: 6 }}>
                {action.reason || 'No reason provided'}
              </div>
              {action.moderator && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  By: {action.moderator.username || action.moderator.email}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderModeratorActivities = () => {
    if (moderatorActivitiesLoading) {
      return <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>Loading moderator activities...</p>;
    }

    return (
      <div>
        {/* Admin Panel Button */}
        {isModerator && (
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <button
              onClick={() => { try { window.location.href = '/admin'; } catch { } }}
              style={{
                width: '100%',
                padding: '12px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              Go to Admin Panel
            </button>
          </div>
        )}

        {/* Moderator Activities List */}
        {!moderatorActivities?.length ? (
          <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
            <p>No moderator activities found.</p>
            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
              This section shows moderation actions performed by this moderator, such as content deletions or warnings issued to other users.
            </p>
          </div>
        ) : (
          <div style={{ padding: '16px' }}>
            {moderatorActivities.map((activity) => {
              const when = new Date(activity.createdAt).toLocaleDateString();
              const preview = activity.contentPreview || '';
              const id = activity.contentId || activity.targetId || '';
              const href = activity.contentType === 'TOPIC' ? `/t/${id}` : '';
              return (
                <div key={activity.id} style={{
                  padding: '16px', backgroundColor: 'white', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 12
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', textTransform: 'uppercase' }}>{activity.actionType}</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{activity.contentType}</span>
                      {preview && <span style={{ fontSize: 12, color: '#6b7280' }}>— "{preview}"</span>}
                      {id && href && activity.actionType !== 'DELETE' && <a href={href} className="btn btn-ghost" style={{ fontSize: 12 }}>Open</a>}
                    </div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{when}</span>
                  </div>
                  <div style={{ fontSize: 14, color: '#374151' }}>{activity.reason || 'No reason provided'}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleDeleteDraft = (draft: DraftItem) => {
    const confirmed = window.confirm('Delete this draft?');
    if (!confirmed) return;
    if (draft.type === 'topic') {
      onDeleteTopicDraft?.(draft.id);
    } else {
      onDeleteCommentDraft?.(draft.topicId);
    }
  };

  const renderDrafts = () => {
    if (!canViewDrafts) {
      return <p style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>Drafts are private to the owner.</p>;
    }

    if (!drafts.length) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>
          <p>No drafts saved yet.</p>
          <p style={{ fontSize: 13, marginTop: 6 }}>Save a topic or comment as a draft to pick up where you left off.</p>
        </div>
      );
    }

    return (
      <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {drafts.map((draft) => {
          const updatedLabel = draft.updatedAt ? new Date(draft.updatedAt).toLocaleString() : '';
          const locationLabel = draft.location ? `${draft.location.lat.toFixed(4)}, ${draft.location.lng.toFixed(4)}` : null;
          const headline = draft.type === 'topic'
            ? (draft.title || 'Untitled topic draft')
            : (draft.topicTitle || 'Comment draft');
          const body = (draft.content || '').slice(0, 200);
          return (
            <div key={draft.id} style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '12px', background: 'white', display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{draft.type === 'topic' ? 'Topic draft' : 'Comment draft'}</div>
                <div style={{ fontWeight: 600, fontSize: 15, color: '#111827', marginTop: 4, wordBreak: 'break-word' }}>{headline}</div>
                {body && <div style={{ fontSize: 13, color: '#374151', marginTop: 6, wordBreak: 'break-word' }}>{body}{draft.content && draft.content.length > 200 ? '…' : ''}</div>}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8, color: '#6b7280', fontSize: 12 }}>
                  {locationLabel && <span>📍 {locationLabel}</span>}
                  {updatedLabel && <span>Last updated {updatedLabel}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    onResumeDraft?.(draft);
                    onCloseProfile?.();
                  }}
                >
                  Resume
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => handleDeleteDraft(draft)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSaved = () => {
    if (savesLoading) {
      return <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>Loading saves...</p>;
    }

    // Separate pinned and unpinned lists
    const pinnedLists = saveLists.filter(list => list.isPinned);
    const unpinnedLists = saveLists.filter(list => !list.isPinned);

    // Filter unlisted saves based on visibility
    const visibleUnlistedSaves = unlistedSaves.filter(canViewSavedItem);

    // Filter saves in lists and remove empty lists for non-owners
    const filterListSaves = (list: SaveList & { saves: Save[] }) => {
      const visibleSaves = (list.saves || []).filter(canViewSavedItem);
      return { ...list, saves: visibleSaves };
    };

    const visiblePinnedLists = pinnedLists.map(filterListSaves).filter(list =>
      isOwnProfile || (list.saves && list.saves.length > 0)
    );
    const visibleUnpinnedLists = unpinnedLists.map(filterListSaves).filter(list =>
      isOwnProfile || (list.saves && list.saves.length > 0)
    );

    // Calculate total visible saves
    const totalVisibleSaves = visibleUnlistedSaves.length +
      visiblePinnedLists.reduce((acc, list) => acc + (list.saves?.length || 0), 0) +
      visibleUnpinnedLists.reduce((acc, list) => acc + (list.saves?.length || 0), 0);

    if (totalVisibleSaves === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
          <p>{isOwnProfile ? 'No saved items yet.' : 'No public saved items.'}</p>
          {isOwnProfile && (
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              Save topics and comments by clicking the save button to organize them into lists for later reading.
            </p>
          )}
        </div>
      );
    }

    return (
      <div style={{ padding: '16px' }}>
        {/* Pinned Save Lists */}
        {visiblePinnedLists.map((list) => (
          <div key={list.id} style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
              {list.name} ({list.saves?.length || 0})
          </h4>

            {/* List Action Buttons - Only show for own profile */}
            {isOwnProfile && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <button
                  onClick={() => handleTogglePin(list.id)}
                  title={list.isPinned ? 'Unpin list' : 'Pin list'}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: list.isPinned ? '#fef3c7' : 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = list.isPinned ? '#fde68a' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = list.isPinned ? '#fef3c7' : 'white';
                  }}
                >
                  <ThumbtackIcon size={14} /> {list.isPinned ? 'Unpin' : 'Pin'}
                </button>
                <button
                  onClick={() => {
                    setShowRenameModal(list.id);
                    setRenameValue(list.name);
                  }}
                  title="Rename list"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <EditIcon size={14} /> Rename
                </button>
                <button
                  onClick={() => {
                    setActiveTab('saved'); // Ensure we stay on saved tab
                    onSetShowAccessRightsModal?.(list.id);
                  }}
                  title="Manage privacy settings"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <LockIcon size={14} /> Privacy
                </button>
                <button
                  onClick={() => handleShareList(list.id, list.name)}
                  title="Share list"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <ShareIcon size={14} /> Share
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(list.id)}
                  title="Delete list"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#dc2626',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <DeleteIcon size={14} /> Delete
                </button>
              </div>
            )}

            {list.description && (
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>{list.description}</p>
            )}
            <div>
              {list.saves?.map((save) => (
                <div
                  key={save.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    position: 'relative',
                    transition: 'background-color 0.2s, border-color 0.2s'
                  }}
                >
                  {isOwnProfile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSave(save.id, savesApi.getContentDisplayName(save));
                      }}
                      title="Remove from saves"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#6b7280',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        lineHeight: '1',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.borderColor = '#dc2626';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      ×
                    </button>
                  )}
                  <>
                    <div
                      onClick={() => {
                        // Handle navigation based on content type
                        if (save.topic) {
                          handleSavedTopicClick(save);
                        } else if (save.comment) {
                          handleSavedCommentClick(save);
                        }
                      }}
                      style={{
                        cursor: (save.topic && loadingProfileTopicId === save.topic.id) || (save.comment && loadingProfileCommentId === save.comment!.id) ? 'wait' : 'pointer'
                      }}
                    >
                      {save.comment ? (
                        <>
                          <LinkifiedText
                            as="p"
                            text={
                              expandedComments.has(save.id) || save.comment.content.length <= 200
                                ? save.comment.content
                                : save.comment.content.slice(0, 200) + '...'
                            }
                            style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: '1.5', paddingRight: isOwnProfile ? '32px' : '0' }}
                          />
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            on "{save.comment.topic.title}"
                          </p>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: save.note ? '8px' : '0' }}>
                            saved {new Date(save.createdAt).toLocaleDateString()}
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#111827', paddingRight: isOwnProfile ? '32px' : '0' }}>
                            {savesApi.getContentDisplayName(save)}
                          </h5>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: save.note ? '8px' : '0' }}>
                            {save.contentType.toLowerCase()} • saved {new Date(save.createdAt).toLocaleDateString()}
                          </p>
                        </>
                      )}
                      {save.note && (
                        <p style={{ fontSize: '13px', color: '#4b5563', fontStyle: 'italic' }}>{save.note}</p>
                      )}
                      {((save.topic && loadingProfileTopicId === save.topic.id) || (save.comment && loadingProfileCommentId === save.comment.id)) && (
                        <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                          Loading…
                        </div>
                      )}
                    </div>
                    {save.comment && save.comment.content.length > 200 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedComments(prev => {
                            const next = new Set(prev);
                            if (next.has(save.id)) {
                              next.delete(save.id);
                            } else {
                              next.add(save.id);
                            }
                            return next;
                          });
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          color: '#3b82f6',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        {expandedComments.has(save.id) ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Quick Saves (unlisted items) */}
        {visibleUnlistedSaves.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
              Quick Saves ({visibleUnlistedSaves.length})
            </h4>
            <div>
              {visibleUnlistedSaves.map((save) => (
                <div
                  key={save.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    position: 'relative',
                    transition: 'background-color 0.2s, border-color 0.2s'
                  }}
                >
                  {isOwnProfile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSave(save.id, savesApi.getContentDisplayName(save));
                      }}
                      title="Remove from saves"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#6b7280',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        lineHeight: '1',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.borderColor = '#dc2626';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      ×
                    </button>
                  )}
                  <>
                    <div
                      onClick={() => {
                        // Handle navigation based on content type
                        if (save.topic) {
                          handleSavedTopicClick(save);
                        } else if (save.comment) {
                          handleSavedCommentClick(save);
                        }
                      }}
                      style={{
                        cursor: (save.topic && loadingProfileTopicId === save.topic.id) || (save.comment && loadingProfileCommentId === save.comment!.id) ? 'wait' : 'pointer'
                      }}
                    >
                      {save.comment ? (
                        <>
                          <LinkifiedText
                            as="p"
                            text={
                              expandedComments.has(save.id) || save.comment.content.length <= 200
                                ? save.comment.content
                                : save.comment.content.slice(0, 200) + '...'
                            }
                            style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: '1.5', paddingRight: isOwnProfile ? '32px' : '0' }}
                          />
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            on "{save.comment.topic.title}"
                          </p>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: save.note ? '8px' : '0' }}>
                            saved {new Date(save.createdAt).toLocaleDateString()}
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#111827', paddingRight: isOwnProfile ? '32px' : '0' }}>
                            {savesApi.getContentDisplayName(save)}
                          </h5>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: save.note ? '8px' : '0' }}>
                            {save.contentType.toLowerCase()} • saved {new Date(save.createdAt).toLocaleDateString()}
                          </p>
                        </>
                      )}
                      {save.note && (
                        <p style={{ fontSize: '13px', color: '#4b5563', fontStyle: 'italic' }}>{save.note}</p>
                      )}
                      {((save.topic && loadingProfileTopicId === save.topic.id) || (save.comment && loadingProfileCommentId === save.comment.id)) && (
                        <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                          Loading…
                        </div>
                      )}
                    </div>
                    {save.comment && save.comment.content.length > 200 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedComments(prev => {
                            const next = new Set(prev);
                            if (next.has(save.id)) {
                              next.delete(save.id);
                            } else {
                              next.add(save.id);
                            }
                            return next;
                          });
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          color: '#3b82f6',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        {expandedComments.has(save.id) ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Unpinned Save Lists */}
        {visibleUnpinnedLists.map((list) => (
          <div key={list.id} style={{ marginBottom: '24px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
              {list.name} ({list.saves?.length || 0})
          </h4>

            {/* List Action Buttons - Only show for own profile */}
            {isOwnProfile && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '12px'
              }}>
                <button
                  onClick={() => handleTogglePin(list.id)}
                  title={list.isPinned ? 'Unpin list' : 'Pin list'}
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: list.isPinned ? '#fef3c7' : 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = list.isPinned ? '#fde68a' : '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = list.isPinned ? '#fef3c7' : 'white';
                  }}
                >
                  <ThumbtackIcon size={14} /> {list.isPinned ? 'Unpin' : 'Pin'}
                </button>
                <button
                  onClick={() => {
                    setShowRenameModal(list.id);
                    setRenameValue(list.name);
                  }}
                  title="Rename list"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <EditIcon size={14} /> Rename
                </button>
                <button
                  onClick={() => {
                    setActiveTab('saved'); // Ensure we stay on saved tab
                    onSetShowAccessRightsModal?.(list.id);
                  }}
                  title="Manage privacy settings"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <LockIcon size={14} /> Privacy
                </button>
                <button
                  onClick={() => handleShareList(list.id, list.name)}
                  title="Share list"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <ShareIcon size={14} /> Share
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(list.id)}
                  title="Delete list permanently"
                  style={{
                    padding: '6px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    color: '#dc2626',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <DeleteIcon size={14} /> Delete
                </button>
              </div>
            )}

            {list.description && (
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>{list.description}</p>
            )}
            <div>
              {list.saves?.map((save) => (
                <div
                  key={save.id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    position: 'relative',
                    transition: 'background-color 0.2s, border-color 0.2s'
                  }}
                >
                  {isOwnProfile && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSave(save.id, savesApi.getContentDisplayName(save));
                      }}
                      title="Remove from saves"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        color: '#6b7280',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        lineHeight: '1',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.borderColor = '#dc2626';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      ×
                    </button>
                  )}
                  <>
                    <div
                      onClick={() => {
                        // Handle navigation based on content type
                        if (save.topic) {
                          handleSavedTopicClick(save);
                        } else if (save.comment) {
                          handleSavedCommentClick(save);
                        }
                      }}
                      style={{
                        cursor: (save.topic && loadingProfileTopicId === save.topic.id) || (save.comment && loadingProfileCommentId === save.comment!.id) ? 'wait' : 'pointer'
                      }}
                    >
                      {save.comment ? (
                        <>
                          <LinkifiedText
                            as="p"
                            text={
                              expandedComments.has(save.id) || save.comment.content.length <= 200
                                ? save.comment.content
                                : save.comment.content.slice(0, 200) + '...'
                            }
                            style={{ margin: '0 0 8px', fontSize: '14px', lineHeight: '1.5', paddingRight: isOwnProfile ? '32px' : '0' }}
                          />
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            on "{save.comment.topic.title}"
                          </p>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: save.note ? '8px' : '0' }}>
                            saved {new Date(save.createdAt).toLocaleDateString()}
                          </p>
                        </>
                      ) : (
                        <>
                          <h5 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', color: '#111827', paddingRight: isOwnProfile ? '32px' : '0' }}>
                            {savesApi.getContentDisplayName(save)}
                          </h5>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: save.note ? '8px' : '0' }}>
                            {save.contentType.toLowerCase()} • saved {new Date(save.createdAt).toLocaleDateString()}
                          </p>
                        </>
                      )}
                      {save.note && (
                        <p style={{ fontSize: '13px', color: '#4b5563', fontStyle: 'italic' }}>{save.note}</p>
                      )}
                      {((save.topic && loadingProfileTopicId === save.topic.id) || (save.comment && loadingProfileCommentId === save.comment.id)) && (
                        <div style={{ fontSize: '12px', color: '#2563eb', marginTop: '4px' }}>
                          Loading…
                        </div>
                      )}
                    </div>
                    {save.comment && save.comment.content.length > 200 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedComments(prev => {
                            const next = new Set(prev);
                            if (next.has(save.id)) {
                              next.delete(save.id);
                            } else {
                              next.add(save.id);
                            }
                            return next;
                          });
                        }}
                        style={{
                          marginTop: '8px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          color: '#3b82f6',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        {expandedComments.has(save.id) ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ height: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading profile...</div>
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 120px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Profile Header */}
      <div style={{
        padding: '20px 16px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: 'white'
      }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            backgroundColor: '#e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </div>

          {/* Follow / Message controls */}
          {!isOwnProfile && targetUserId && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}
            >
              <button
                type="button"
                onClick={handleToggleFollow}
                disabled={followPending}
                style={{
                  border: '1px solid',
                  borderColor: profile?.isFollowing ? '#2563eb' : '#e5e7eb',
                  background: profile?.isFollowing ? '#2563eb' : 'white',
                  color: profile?.isFollowing ? 'white' : '#111827',
                  padding: '8px 12px',
                  borderRadius: 8,
                  cursor: followPending ? 'wait' : 'pointer',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  flex: '0 0 auto'
                }}
              >
                {followPending ? 'Updating…' : profile?.isFollowing ? 'Following' : 'Follow'}
              </button>
              <button
                type="button"
                title="Message user"
                onClick={() => {
                  if (!currentUser?.id) {
                    try {
                      const dest = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
                      window.location.href = dest;
                    } catch { }
                    return;
                  }
                  try { onMessageUser && onMessageUser(targetUserId); } catch { }
                }}
                style={{
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#2563eb',
                  padding: '8px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  flex: '0 0 auto'
                }}
              >
                <MessagePlusIcon size={16} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Message</span>
              </button>
            </div>
          )}
          {isOwnProfile && onOpenMessages && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}
            >
              <button
                type="button"
                title="Open messages"
                onClick={() => {
                  try { onOpenMessages(); } catch { }
                }}
                style={{
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#2563eb',
                  padding: '8px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  flex: '0 0 auto'
                }}
              >
                <ChatIcon size={16} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Messages</span>
              </button>
            </div>
          )}

          <div
            style={{
              flex: '1 1 260px',
              minWidth: '220px'
            }}
          >
            <h2 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '600' }}>
              {profile?.user?.username || profile?.user?.email?.split('@')[0] || 'User'}
            </h2>
            <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
              {isOwnProfile ? profile?.user?.email : 'Public Profile'}
            </p>
            <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: '#6b7280', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                ⭐ {profile?.user?.reputation ?? 0}
                {(() => {
                  const tier = getReputationTier(profile?.user?.reputation);
                  if (!tier) return null;
                  return (
                    <span
                      title={`Earned through appreciation from other users (${tier.min}+ reputation)`}
                      style={{ background: tier.background, color: tier.color, border: `1px solid ${tier.border}`, borderRadius: 9999, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}
                    >
                      {tier.name}
                    </span>
                  );
                })()}
              </span>
              <span>{profile?.topicCount || 0} topics</span>
              <span>{profile?.commentCount || 0} comments</span>
              <span>{profile?.followerCount ?? 0} followers</span>
              <span>{profile?.followingCount ?? 0} following</span>
              <span>Member since {profile?.user?.createdAt ? new Date(profile.user.createdAt).toLocaleDateString() : 'Unknown'}</span>
            </div>
            {!isOwnProfile && profile?.isFollowedBy && (
              <div style={{ fontSize: '12px', marginTop: '4px', color: '#059669' }}>Follows you</div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Tabs */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 0',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: 'white',
        padding: '0 8px'
      }}>
        <button
          onClick={() => setActiveTab('topics')}
          style={{
            padding: '10px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            borderBottom: activeTab === 'topics' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'topics' ? '#3b82f6' : '#6b7280',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            flexShrink: 0
          }}
        >
          Topics ({profile?.topicCount || 0})
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          style={{
            padding: '10px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            borderBottom: activeTab === 'comments' ? '2px solid #3b82f6' : '2px solid transparent',
            color: activeTab === 'comments' ? '#3b82f6' : '#6b7280',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            flexShrink: 0
          }}
        >
          Comments ({profile?.commentCount || 0})
        </button>
        {canViewDrafts && (
          <button
            onClick={() => setActiveTab('drafts')}
            style={{
              padding: '10px 12px',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'drafts' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'drafts' ? '#3b82f6' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            Drafts ({draftCount})
          </button>
        )}
        {canViewSaves && (
          <button
            onClick={() => setActiveTab('saved')}
            style={{
              padding: '10px 12px',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'saved' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'saved' ? '#3b82f6' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            Saved ({activeTab === 'saved' && (saveLists.length > 0 || unlistedSaves.length > 0)
              ? unlistedSaves.filter(canViewSavedItem).length + saveLists.reduce((acc, list) => acc + (list.saves || []).filter(canViewSavedItem).length, 0)
              : savesCount})
          </button>
        )}
        {canViewNotifications && (
          <button
            onClick={() => setActiveTab('notifications')}
            style={{
              padding: '10px 12px',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'notifications' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'notifications' ? '#3b82f6' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            Notifications ({notifications?.filter(n => !n.read).length || 0})
          </button>
        )}
        {canViewModeration && (
          <button
            onClick={() => setActiveTab('moderation')}
            style={{
              padding: '10px 12px',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'moderation' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'moderation' ? '#3b82f6' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            Moderation History
          </button>
        )}
        {profileUserIsModerator && isOwnProfile && (
          <button
            onClick={() => setActiveTab('mod-activities')}
            style={{
              padding: '10px 12px',
              border: 'none',
              backgroundColor: 'transparent',
              borderBottom: activeTab === 'mod-activities' ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === 'mod-activities' ? '#3b82f6' : '#6b7280',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              flexShrink: 0
            }}
          >
            Moderator Activities
            {isModerator && isOwnProfile && pendingReports > 0 && (
              <span style={{
                marginLeft: '8px',
                background: '#dc2626',
                color: '#fff',
                borderRadius: '9999px',
                fontSize: '11px',
                padding: '2px 6px',
                minWidth: '16px',
                display: 'inline-block',
                textAlign: 'center'
              }} title={`${pendingReports} pending reports`}>
                {pendingReports}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Account Settings Link - Only show for own profile */}
      {isOwnProfile && (
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
          <a
            href="/settings"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#6b7280',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
            </svg>
            Account Settings
          </a>
        </div>
      )}

      {/* Profile Content */}
      <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#f9fafb', padding: showAccessRightsModal ? '24px' : '0' }}>
        {showAccessRightsModal ? (
          <AccessRightsModal
            listId={showAccessRightsModal}
            list={saveLists.find(l => l.id === showAccessRightsModal)}
            onClose={() => onSetShowAccessRightsModal?.(null)}
            onUpdate={handleUpdateAccessRights}
          />
        ) : (
          <>
            {activeTab === 'topics' && renderTopics()}
            {activeTab === 'comments' && renderComments()}
            {activeTab === 'drafts' && renderDrafts()}
            {activeTab === 'saved' && renderSaved()}
            {activeTab === 'notifications' && renderNotifications()}
            {activeTab === 'moderation' && renderModeration()}
            {activeTab === 'mod-activities' && renderModeratorActivities()}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowDeleteConfirm(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Delete List</h3>
            <p style={{ margin: '0 0 24px 0', color: '#6b7280' }}>
              Are you sure you want to delete this list? This action cannot be undone and all saves in the list will be moved to quick saves.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteList(showDeleteConfirm)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Delete List
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename List Modal */}
      {showRenameModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowRenameModal(null);
            setRenameValue('');
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Rename List</h3>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                List Name
              </label>
              <input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleRenameList()}
                placeholder="Enter new list name"
                maxLength={100}
                autoFocus
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              <small style={{ display: 'block', marginTop: '4px', color: '#6b7280', fontSize: '12px' }}>
                {renameValue.length}/100 characters
              </small>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowRenameModal(null);
                  setRenameValue('');
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRenameList}
                disabled={!renameValue.trim()}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: renameValue.trim() ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  cursor: renameValue.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileContentPanel;
